// Resolves which Shipium domain the running bundle belongs to, based purely on
// the hostname it was served from. The production bundle carries no build-time
// pointer to its backend, so every cross-app URL is derived at runtime from
// window.location.
//
// Hostnames look like:
//   partner.shipium.com               -> NA prod, no region suffix
//   partner-us-west-2.shipium.com     -> NA prod, pinned to a region
//   partner.stage.shipium.com         -> NA stage
//   partner.shipium.co.uk             -> EU prod
//   partner-eu-west-2.stage.shipium.co.uk -> EU stage, pinned to a region
//   partner.local-internal.com        -> local dev

export const SHIPIUM_DOMAINS = {
  NA: 'shipium.com',
  EU: 'shipium.co.uk',
} as const;

export type ShipiumDomain = (typeof SHIPIUM_DOMAINS)[keyof typeof SHIPIUM_DOMAINS];

// Regions a hostname may pin itself to. A bare hostname (partner.shipium.com)
// resolves to no suffix and is routed to the nearest region by DNS.
const PINNABLE_REGIONS = ['us-west-2', 'us-east-1', 'eu-west-2'] as const;

const LOCAL_DOMAIN = 'local-internal.com';

// Anchored on a dot boundary so a host that merely contains the apex
// (shipium.co.uk.example.net) resolves to NA rather than EU.
const EU_APEX_PATTERN = new RegExp(`(^|\\.)${SHIPIUM_DOMAINS.EU.replace(/\./g, '\\.')}$`);

export interface ShipiumDomainInfo {
  // Apex domain the current deployment is served from, e.g. 'shipium.co.uk'.
  domain: ShipiumDomain;
  // '-eu-west-2' when the hostname pins a region, otherwise ''.
  regionSuffix: string;
  isEu: boolean;
  isLocal: boolean;
  isStage: boolean;
}

// A trailing dot is a legal absolute-FQDN form that reaches the same
// deployment, so it must not change which domain a hostname resolves to.
const normalizeHostname = (hostname: string) => hostname.replace(/\.$/, '');

// Host to hang a sibling app's subdomain off, e.g. 'stage.shipium.co.uk'.
//
// Stage takes precedence over local: a hostname carrying a stage label resolves
// to the stage host of its domain even when it also looks local.
//
// matchBareLocalhost widens what counts as local from `local-internal` to any
// `local` hostname, so a bare localhost resolves to the local domain. Callers
// that serve embedded content from the local dev host opt in; callers building
// backend origins leave it off, so an unrecognized host resolves to a real
// deployment rather than a dev-only one.
export function getShipiumBaseHost(hostname: string, matchBareLocalhost = false): string {
  const normalized = normalizeHostname(hostname);
  const { domain, isLocal, isStage } = getShipiumDomain(normalized);
  if (isStage) return `stage.${domain}`;
  if (isLocal || (matchBareLocalhost && /local/.test(normalized))) return LOCAL_DOMAIN;
  return domain;
}

export function getShipiumDomain(hostname: string): ShipiumDomainInfo {
  const normalized = normalizeHostname(hostname);
  const isEu = EU_APEX_PATTERN.test(normalized);
  const region = PINNABLE_REGIONS.find((candidate) => normalized.includes(candidate));

  return {
    domain: isEu ? SHIPIUM_DOMAINS.EU : SHIPIUM_DOMAINS.NA,
    regionSuffix: region ? `-${region}` : '',
    isEu,
    isLocal: /local-internal/.test(normalized),
    isStage: /stage/.test(normalized),
  };
}
